import { DeleteOutlined, EditOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
    Box,
    Button,
    Divider,
    FormControlLabel,
    FormHelperText,
    IconButton,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import useActionDialog from "../../components/ActionDialog";
import SideSheet, {
    SideSheetButtons,
    SideSheetContent,
    SideSheetForm,
    useSideSheet,
} from "../../components/SideSheet";
import {
    FormFieldErrors,
    hasErrors,
    useForm,
    validateMandatoryField,
    validateNumberField,
} from "../../utils/form";
import { useUserInfo } from "../../utils/security";
import { wait } from "../../utils/mock";
import NumberField from "../../components/NumberField";
import { isEmpty } from "lodash";
import { formatNum, parseNum } from "../../utils/numbers";

export const TaxSettingsCard = ({ children }) => {
    const { userInfo, updateUserInfo, userInfoLoading: progress } = useUserInfo();
    const taxRates = userInfo.taxRates || [];

    const taxRateSettingsModal = useSideSheet();
    const showDialog = useActionDialog();

    const activeTaxRates = (taxRates || []).filter((item) => !item.hidden);
    const hiddenTaxRates = (taxRates || []).filter((item) => item.hidden);
    const showTitles = !isEmpty(hiddenTaxRates);

    const hideTaxRate = (taxRate) => async () => {
        await wait(3500);
    };

    const deleteTaxRate = (taxRate) => async () => {
        await wait(2500);
    };

    return (
        <>
            <Box sx={{ mb: 6 }}>
                {showTitles && (
                    <Typography variant="h6" gutterBottom>
                        Sazby daně
                    </Typography>
                )}
                {!isEmpty(activeTaxRates) && (
                    <Divider sx={(theme) => ({ borderColor: theme.palette.outlineVariant.main })} />
                )}
                {activeTaxRates.map((taxRate) => (
                    <Box key={taxRate.id}>
                        <Box sx={{ display: "flex", gap: 2, my: 1, alignItems: "center" }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" gutterBottom>
                                    {taxRate.name}
                                </Typography>
                                <Typography variant="body2" gutterBottom color="text.secondary">
                                    {taxRate.type === "PERCENTAGE"
                                        ? `Sazba: ${taxRate.percentage}%`
                                        : undefined}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    flex: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                }}>
                                <IconButton
                                    onClick={() => taxRateSettingsModal.show(taxRate)}
                                    title="Upravit">
                                    <EditOutlined />
                                </IconButton>
                                <IconButton
                                    onClick={() =>
                                        showDialog(
                                            "Smazat sazbu daně?",
                                            `Sazba daně: ${taxRate.name}`,
                                            [
                                                { title: "Storno" },
                                                {
                                                    title: "Smazat sazbu",
                                                    variant: "outlined",
                                                    primary: true,
                                                    onClick: deleteTaxRate(taxRate),
                                                },
                                                {
                                                    title: "Skrýt sazbu",
                                                    variant: "contained",
                                                    primary: true,
                                                    onClick: hideTaxRate(taxRate),
                                                },
                                            ],
                                            {
                                                helperText:
                                                    "Při změně daňových sazeb doporučujeme vytvořit novou sazbu a starou skrýt. Skryté sazby se nebudou zobrazovat v nabídkách a formulářích, ale zůstanou zachovány v již vytvořených přehledech a podáních. Pokud se rozhodnete smazat sazbu, bude smazána ze všech transakcí, kde je použita.",
                                            }
                                        )
                                    }
                                    title="Smazat">
                                    <DeleteOutlined />
                                </IconButton>
                            </Box>
                        </Box>
                        <Divider
                            sx={(theme) => ({ borderColor: theme.palette.outlineVariant.main })}
                        />
                    </Box>
                ))}
            </Box>
            {showTitles && (
                <Typography variant="h6" gutterBottom>
                    Skryté sazby daně
                </Typography>
            )}
            {!isEmpty(hiddenTaxRates) && (
                <Divider sx={(theme) => ({ borderColor: theme.palette.outlineVariant.main })} />
            )}
            {hiddenTaxRates.map((taxRate) => (
                <Box key={taxRate.id}>
                    <Box sx={{ display: "flex", gap: 2, my: 1, alignItems: "center" }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" gutterBottom color="text.disabled">
                                {taxRate.name}
                            </Typography>
                            <Typography variant="body2" gutterBottom color="text.disabled">
                                {taxRate.type === "PERCENTAGE"
                                    ? `Sazba: ${taxRate.percentage}%`
                                    : undefined}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                flex: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                            }}>
                            <IconButton
                                onClick={() => taxRateSettingsModal.show(taxRate)}
                                title="Upravit">
                                <EditOutlined />
                            </IconButton>
                            <IconButton
                                onClick={() =>
                                    showDialog(
                                        "Smazat sazbu daně?",
                                        `Sazba daně: ${taxRate.name}`,
                                        [
                                            { title: "Storno" },
                                            {
                                                title: "Smazat sazbu",
                                                variant: "contained",
                                                primary: true,
                                                onClick: deleteTaxRate(taxRate),
                                            },
                                        ]
                                    )
                                }
                                title="Smazat">
                                <DeleteOutlined />
                            </IconButton>
                        </Box>
                    </Box>
                    <Divider sx={(theme) => ({ borderColor: theme.palette.outlineVariant.main })} />
                </Box>
            ))}
            <TaxSettingsModal
                open={taxRateSettingsModal.open}
                data={taxRateSettingsModal.data}
                hide={taxRateSettingsModal.hide}
                onSettingsSaved={async (data) => {
                    //await updateUserInfo({});
                    // TODO implement properly
                    //refreshList();
                    //refreshDetail();
                    //showSnackbar("Uloženo");
                    //TODO handle error?? or leave it to the form?
                    throw new Error();

                    taxRateSettingsModal.hide();
                }}
            />
        </>
    );
};
const TaxSettingsModal = ({ data, onSettingsSaved, open, hide }) => {
    const userInfo = useUserInfo();
    const [loading, setLoading] = useState(false);

    const onSubmit = useCallback(
        async (data) => {
            setLoading(true);
            try {
                //TODO update user info mock
                await wait(4000);
                onSettingsSaved && (await onSettingsSaved(data));
            } finally {
                setLoading(false);
            }
        },
        [onSettingsSaved, setLoading]
    );

    const validate = useCallback((values, errors) => {
        validateMandatoryField(values.name, "name", errors);
        /*
        validateMandatoryField(values.mode, "type", errors);
        if (values.mode === "PERCENTAGE") {
            validateMandatoryField(values.percentage, "percentage", errors);
        } else if (values.mode === "PER_UNIT") {
            validateMandatoryField(values.unitAmount, "unitAmount", errors);
            validateMandatoryField(values.unit, "unit", errors);
        } else if (values.mode === "FIXED") {
            validateMandatoryField(values.fixedAmount, "fixedAmount", errors);
        }
        */
        validateMandatoryField(values.percentage, "percentage", errors);
        validateNumberField(values.percentage, "percentage", errors);
        return errors;
    }, []);

    const mapFieldsToData = useCallback(
        ({ percentage, ...values }) => ({
            percentage: parseNum(percentage, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            ...values,
        }),
        [userInfo]
    );
    const mapDataToFields = useCallback(
        ({ percentage, ...values }) => ({
            percentage: formatNum(
                percentage,
                userInfo.decimalDelimiter,
                userInfo.thousandDelimiter
            ),
            ...values,
        }),
        [userInfo]
    );

    const { handleSubmit, handleChange, values, errors } = useForm({
        onSubmit: onSubmit,
        validate,
        data,
        mapFieldsToData,
        mapDataToFields,
    });

    return (
        <>
            <SideSheet title="Sazba daně" open={open} hide={hide} loading={loading}>
                <SideSheetForm onSubmit={handleSubmit}>
                    <SideSheetContent>
                        <TextField
                            value={values.name || ""}
                            onChange={handleChange}
                            disabled={loading}
                            name="name"
                            label="Název"
                            placeholder="Název sazby daně"
                            fullWidth
                            margin="normal"
                            required
                            error={hasErrors(errors, "name")}
                            helperText={<FormFieldErrors errors={errors} field="name" />}
                        />

                        <NumberField
                            value={values.percentage || ""}
                            onChange={handleChange}
                            disabled={loading}
                            name="percentage"
                            label="Výše sazby"
                            placeholder="Zadejte procentuální sazbu daně"
                            unit="%"
                            fullWidth
                            margin="normal"
                            required
                            error={hasErrors(errors, "percentage")}
                            helperText={<FormFieldErrors errors={errors} field="percentage" />}
                        />
                        <TextField
                            value={values.description || ""}
                            onChange={handleChange}
                            disabled={loading}
                            name="description"
                            label="Popis"
                            placeholder="Více informací o sazbě daně. Např. proč je použita, jak se vypočítává atd. Tento popis slouží pro usnadnění práce s daní."
                            fullWidth
                            multiline
                            rows={4}
                            margin="normal"
                            helperText={<FormFieldErrors errors={errors} field="description" />}
                        />
                        <Box my={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={values.hidden || false}
                                        onChange={handleChange}
                                        name="hidden"
                                    />
                                }
                                label="Skrytá sazba"
                                disabled={loading}
                                margin="normal"
                                sx={{ mb: 1 }}
                            />
                            <FormHelperText>
                                Při změně daňových sazeb doporučujeme vytvořit novou sazbu a starou
                                skrýt. Skryté sazby se nebudou zobrazovat v nabídkách a formulářích,
                                ale zůstanou zachovány v již vytvořených přehledech a podáních
                            </FormHelperText>
                        </Box>

                        {/*
            <FormControl disabled={loading} fullWidth margin="normal">
                <FormLabel>Typ daně</FormLabel>
                <RadioGroup name="type" value={values.type} onChange={handleChange}>
                    <FormControlLabel value="PERCENTAGE" control={<Radio />} label="Procentuální sazba" />
                    <FormControlLabel value="PER_UNIT" control={<Radio />} label="Za jednotku" />
                    <FormControlLabel value="FIXED" control={<Radio />} label="Fixní sazba" />
                </RadioGroup>
                <FormHelperText>
                    <FormFieldErrors errors={errors} field="type" />
                </FormHelperText>
            </FormControl>

            {values.type === "PERCENTAGE" && (
                <TextField
                    value={values.percentage}
                    onChange={handleChange}
                    disabled={loading}
                    name="percentage"
                    label="Výše sazby"
                    placeholder="Zadejte procentuální sazbu daně"
                    fullWidth
                    margin="normal"
                    required
                    helperText={<FormFieldErrors errors={errors} field="percentage" />}
                />
            )}
            {values.type === "PER_UNIT" && (
                <>
                    <TextField
                        value={values.unitAmount}
                        onChange={handleChange}
                        disabled={loading}
                        name="unitAmount"
                        label="Výše sazby za jednotku"
                        placeholder="Zadejte výši daně za jednotku"
                        fullWidth
                        margin="normal"
                        required
                        helperText={<FormFieldErrors errors={errors} field="unitAmount" />}
                    />
                    <TextField
                        value={values.unit}
                        onChange={handleChange}
                        disabled={loading}
                        name="unit"
                        label="Jednotka"
                        placeholder="Zadejte jednotku"
                        fullWidth
                        margin="normal"
                        required
                        helperText={<FormFieldErrors errors={errors} field="unit" />}
                    />
                </>
            )}
            <pre>{JSON.stringify(values, null, 4)}</pre>
        */}
                    </SideSheetContent>
                    <SideSheetButtons>
                        <Button
                            variant="outlined"
                            onClick={(e) => hide && hide()}
                            disabled={loading}>
                            Storno
                        </Button>
                        <LoadingButton
                            variant="contained"
                            type="submit"
                            disabled={loading}
                            loading={loading}>
                            Uložit
                        </LoadingButton>
                    </SideSheetButtons>
                </SideSheetForm>
            </SideSheet>
        </>
    );
};
